import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "trusted-account-filter"
}
const _hoisted_2 = { class: "trusted-account-filter__trigger-value" }
const _hoisted_3 = {
  class: "trusted-account-filter__panel",
  role: "dialog",
  "aria-label": "Trust Accounts"
}
const _hoisted_4 = {
  key: 0,
  class: "trusted-account-filter__selectall"
}
const _hoisted_5 = { class: "trusted-account-filter__fields" }
const _hoisted_6 = { class: "trusted-account-filter__list" }
const _hoisted_7 = ["for"]

import {
    computed, onMounted, ref, watch,
} from 'vue';
import Checkbox from 'primevue/checkbox';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import Popover from 'primevue/popover';
import {
    savePaymentTrustedAccountIds,
    type PaymentTrustedAccountSelection,
    usePaymentTrustedAccountOptions,
} from '@/composables/usePaymentTrustedAccountOptions';


export default /*@__PURE__*/_defineComponent({
  __name: 'TrustedAccountFilter',
  props: {
    modelValue: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

/* eslint-env vue/setup-compiler-macros */
// Filter Payments by trusted account (payee) IDs from GET /api/payments/trusted-accounts.
const props = __props;

const emit = __emit;

const popoverRef = ref();
const search = ref('');
const { options, loadOptions } = usePaymentTrustedAccountOptions();

const normalizedSelected = computed(() => {
    if (props.modelValue === null) return [];

    return props.modelValue.filter(
        (id) => options.value.some((option) => option.id === id),
    );
});

// Selections are staged in a draft so the panel filters and closes only on Apply.
const draft = ref<PaymentTrustedAccountSelection>(null);

const draftSelected = computed(() => {
    if (draft.value === null) return [];

    return draft.value.filter(
        (id) => options.value.some((option) => option.id === id),
    );
});

const allSelected = computed(() => (
    options.value.length > 0
    && draft.value === null
));

const partiallySelected = computed(() => (
    draft.value !== null
    && draftSelected.value.length > 0
    && draftSelected.value.length < options.value.length
));

const visibleOptions = computed(() => {
    const term = search.value.trim().toLowerCase();
    if (!term) return options.value;
    return options.value.filter((option) => option.label.toLowerCase().includes(term));
});

const triggerValue = computed(() => {
    if (options.value.length === 0 || props.modelValue === null) return 'All';
    return `${normalizedSelected.value.length} of ${options.value.length}`;
});

function isSelected(id: number): boolean {
    return draft.value === null || draftSelected.value.includes(id);
}

function updateSelection(selection: PaymentTrustedAccountSelection) {
    savePaymentTrustedAccountIds(selection);
    emit('update:modelValue', selection);
}

function syncDraft() {
    draft.value = props.modelValue === null ? null : [...normalizedSelected.value];
}

function updateOptionSelection(id: number, checked: boolean) {
    const selected = draft.value === null
        ? options.value.map((option) => option.id)
        : [...draftSelected.value];
    const next = checked
        ? (selected.includes(id) ? selected : [...selected, id])
        : selected.filter((selectedId) => selectedId !== id);

    draft.value = next.length === options.value.length ? null : next;
}

function selectAll(checked: boolean) {
    draft.value = checked ? null : [];
}

function applySelection() {
    updateSelection(draft.value);
    popoverRef.value?.hide();
}

function cancelSelection() {
    syncDraft();
    popoverRef.value?.hide();
}

// Refreshed before the panel opens rather than on the popover's show event, so the first frame
// already holds the applied selection.
function togglePanel(event: MouseEvent) {
    if (!popoverRef.value?.visible) {
        search.value = '';
        syncDraft();
    }

    popoverRef.value?.toggle(event);
}

watch(options, (nextOptions) => {
    if (nextOptions.length === 0 || props.modelValue === null) return;

    const validIds = new Set(nextOptions.map((option) => option.id));
    const nextSelected = [...new Set(props.modelValue.filter((id) => validIds.has(id)))];
    const nextSelection = nextSelected.length === nextOptions.length ? null : nextSelected;

    if (
        nextSelection === null
        || nextSelection.length !== props.modelValue.length
        || nextSelection.some((id, index) => id !== props.modelValue[index])
    ) {
        updateSelection(nextSelection);
    }
}, { immediate: true });

onMounted(() => {
    void loadOptions();
});

return (_ctx: any,_cache: any) => {
  return (_unref(options).length > 1)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("button", {
          class: "trusted-account-filter__trigger",
          type: "button",
          "aria-haspopup": "dialog",
          onClick: togglePanel
        }, [
          _cache[3] || (_cache[3] = _createElementVNode("span", { class: "trusted-account-filter__trigger-label" }, "Filter by Trust Account:", -1)),
          _createElementVNode("span", _hoisted_2, _toDisplayString(triggerValue.value), 1),
          _cache[4] || (_cache[4] = _createElementVNode("svg", {
            class: "trusted-account-filter__trigger-chevron",
            width: "12",
            height: "7",
            viewBox: "0 0 12 7",
            fill: "none",
            "aria-hidden": "true"
          }, [
            _createElementVNode("path", {
              d: "M1 1L6 6L11 1",
              stroke: "currentColor",
              "stroke-width": "1.5",
              "stroke-linecap": "round",
              "stroke-linejoin": "round"
            })
          ], -1))
        ]),
        _createVNode(_unref(Popover), {
          ref_key: "popoverRef",
          ref: popoverRef,
          pt: { root: { class: 'trusted-account-filter-popover popover-align-end' } }
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, [
              _cache[6] || (_cache[6] = _createElementVNode("div", { class: "trusted-account-filter__title" }, " Trust Account ", -1)),
              _cache[7] || (_cache[7] = _createElementVNode("div", { class: "trusted-account-filter__divider trusted-account-filter__divider--header" }, null, -1)),
              _createVNode(_unref(IconField), { class: "trusted-account-filter__search" }, {
                default: _withCtx(() => [
                  _createVNode(_unref(InputText), {
                    modelValue: search.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
                    type: "text",
                    name: "payment-trusted-account-search",
                    autocomplete: "off",
                    placeholder: "Search for a Trust Account..."
                  }, null, 8, ["modelValue"]),
                  (search.value)
                    ? (_openBlock(), _createBlock(_unref(InputIcon), {
                        key: 0,
                        class: "pi pi-times",
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (search.value = ''))
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }),
              _cache[8] || (_cache[8] = _createElementVNode("div", { class: "trusted-account-filter__divider trusted-account-filter__divider--options" }, null, -1)),
              (!search.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createVNode(_unref(Checkbox), {
                      inputId: "payment-trusted-account-select-all",
                      binary: "",
                      "model-value": allSelected.value,
                      indeterminate: partiallySelected.value,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (selectAll($event)))
                    }, null, 8, ["model-value", "indeterminate"]),
                    _cache[5] || (_cache[5] = _createElementVNode("label", {
                      class: "trusted-account-filter__selectall-label",
                      for: "payment-trusted-account-select-all"
                    }, " Select All ", -1))
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("ul", _hoisted_6, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleOptions.value, (option) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: option.id,
                      class: "trusted-account-filter__item"
                    }, [
                      _createVNode(_unref(Checkbox), {
                        inputId: `payment-trusted-account-${option.id}`,
                        binary: "",
                        "model-value": isSelected(option.id),
                        "onUpdate:modelValue": ($event: any) => (updateOptionSelection(option.id, $event))
                      }, null, 8, ["inputId", "model-value", "onUpdate:modelValue"]),
                      _createElementVNode("label", {
                        class: "trusted-account-filter__item-label",
                        for: `payment-trusted-account-${option.id}`
                      }, _toDisplayString(option.label), 9, _hoisted_7)
                    ]))
                  }), 128))
                ])
              ]),
              _createElementVNode("div", { class: "trusted-account-filter__footer" }, [
                _createElementVNode("button", {
                  type: "button",
                  class: "trusted-account-filter__footer-action trusted-account-filter__footer-action--secondary",
                  onClick: cancelSelection
                }, " Cancel "),
                _createElementVNode("button", {
                  type: "button",
                  class: "trusted-account-filter__footer-action trusted-account-filter__footer-action--primary",
                  onClick: applySelection
                }, " Apply ")
              ])
            ])
          ]),
          _: 1
        }, 512)
      ]))
    : _createCommentVNode("", true)
}
}

})